import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useSettings } from '../../context/SettingsContext';

export default function ProductPage() {
  const { uuid } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, items } = useCart();
  const { settings } = useSettings();
  const inCart = items.some(i => i.uuid === uuid);

  useEffect(() => {
    axios.get(`/api/products/${uuid}`)
      .then(r => setProduct(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [uuid]);

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!product) return <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}><h2>Item not found</h2><Link to="/shop" className="btn btn-navy" style={{ marginTop: 16 }}>Back to Shop</Link></div>;

  const images = product.images?.length ? product.images : [{ url: 'https://via.placeholder.com/600x450?text=No+Image' }];

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '12px 0' }}>
        <div className="container" style={{ fontSize: 13, color: '#9a9a9a', display: 'flex', gap: 6 }}>
          <Link to="/" style={{ color: '#9a9a9a' }}>Home</Link> /
          <Link to="/shop" style={{ color: '#9a9a9a' }}>Shop</Link> /
          {product.category_slug && <Link to={`/shop/${product.category_slug}`} style={{ color: '#9a9a9a' }}>{product.category_name}</Link>}
          {product.category_slug && <span>/</span>}
          <span style={{ color: '#1a2744', fontWeight: 600 }}>{product.title}</span>
        </div>
      </div>

      <div className="container" style={{ padding: '36px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start' }}>

          {/* Images */}
          <div>
            <div style={{ aspectRatio: '4/3', borderRadius: 14, overflow: 'hidden', background: '#f4f4f4', marginBottom: 12 }}>
              <img src={images[activeImg]?.url} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    style={{ flexShrink: 0, width: 72, height: 54, borderRadius: 8, overflow: 'hidden', border: `2px solid ${i === activeImg ? '#c9a227' : '#e8e8e8'}`, cursor: 'pointer', background: 'none', padding: 0 }}>
                    <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {product.new_arrival === 1 && <span className="badge badge-green">New Arrival</span>}
              {product.is_firearm === 1 && <span className="badge badge-firearm">🔒 FFL Transfer Required</span>}
              <span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{product.condition}</span>
            </div>

            <h1 style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 800, color: '#1a2744', marginBottom: 8, lineHeight: 1.3 }}>{product.title}</h1>

            {(product.make || product.model || product.caliber) && (
              <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                {[product.make, product.model, product.caliber && `Cal: ${product.caliber}`].filter(Boolean).join(' · ')}
              </p>
            )}

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 32, fontWeight: 900, color: '#1a2744' }}>${product.price?.toFixed(2)}</span>
              {product.original_price > product.price && (
                <span style={{ fontSize: 18, color: '#9a9a9a', textDecoration: 'line-through' }}>${product.original_price?.toFixed(2)}</span>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 24 }}>
              {product.pickup_only
                ? <>🏪 <strong>In-store pickup only</strong> in {settings.store_city || 'Natchitoches'}</>
                : <>🚚 Shipping ${parseFloat(product.shipping_price ?? settings.default_shipping_price ?? 0).toFixed(2)} · or <strong>free in-store pickup</strong></>}
            </div>

            {/* Cart button */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <button onClick={() => addItem(product)} disabled={inCart}
                className={`btn btn-lg ${inCart ? 'btn-ghost' : 'btn-primary'}`} style={{ flex: 1 }}>
                {inCart ? '✓ Added to Cart' : '🛒 Add to Cart'}
              </button>
              <Link to="/cart" className="btn btn-navy btn-lg">Buy Now</Link>
            </div>

            {/* SKU */}
            {product.bravo_sku && (
              <div style={{ fontSize: 12, color: '#9a9a9a', marginBottom: 16 }}>SKU: {product.bravo_sku}</div>
            )}

            {/* FFL notice */}
            {product.is_firearm === 1 && (
              <div style={{ background: 'rgba(230,81,0,0.06)', border: '1px solid rgba(230,81,0,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#e65100', marginBottom: 6 }}>🔒 FFL Transfer Required</div>
                <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
                  Federal law requires all firearm purchases to be transferred through a licensed FFL dealer in your state.
                  At checkout, you will provide your local FFL dealer's information. We will ship to them directly.
                  You'll complete your ATF Form 4473 and background check at pickup.
                </p>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1a2744', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</h3>
                <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>{product.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Back link */}
        <div style={{ marginTop: 40 }}>
          <Link to={product.category_slug ? `/shop/${product.category_slug}` : '/shop'} style={{ color: '#c9a227', fontWeight: 600, fontSize: 14 }}>
            ← Back to {product.category_name || 'Shop'}
          </Link>
        </div>
      </div>
    </div>
  );
}
